import { ActionReducerMap, MetaReducer, ActionReducer } from '@ngrx/store';
import {
  routerReducer,
  RouterStateSerializer,
  RouterReducerState,
} from '@ngrx/router-store';
import { Injectable } from '@angular/core';

import { storageSync } from '@larscom/ngrx-store-storagesync';

import { Params, RouterStateSnapshot } from '@angular/router';

import * as fromAuth from './auth';
import { clearStateOnAdminLogOut } from './clear-state-on-admin-logout';
import * as fromCognitoAuth from './cognito-auth';
import * as fromCertification from './certification';
import * as fromDevice from './device';
import * as fromFavoriteOccupations from './favorite-occupations';
import * as fromFavoriteMilitaryOccupations from './favorite-military-occupations';
import * as fromGoal from './goal';
import * as fromHotSpots from './hot-spots';
import * as fromInstitution from './institution';
import * as fromInstitutionProgram from './institution-program';
import * as fromLicense from './license';
import * as fromLocation from './location';
import * as fromMaintance from './maintance';
import * as fromMajor from './major';
import * as fromNews from './news';
import * as fromSystemStatus from './system-status';
import * as fromOccupation from './occupation';
import * as fromOccupationDetail from './occupation-detail';
import * as fromOccupationCards from './occupation-cards';
import * as fromPathway from './pathway';
import * as fromPathwayCards from './pathway-cards';
import * as fromPowerBi from './powerbi';
import * as fromTracker from './tracker';
import * as fromUserProfile from './user-profile';
import * as fromError from './error';
import * as fromLoader from './loader';
import * as fromUser from './user';
import * as fromAiChat from './ai-chat';

export interface RouterStateUrl {
  url: string;
  params: Params;
  queryParams: Params;
}

export interface AppState {
  router: RouterReducerState<RouterStateUrl>;
  auth: fromAuth.AuthFeatureState.AuthState;
  cognitoAuth: fromCognitoAuth.CognitoAuthFeatureState.CognitoAuthState;
  certification: fromCertification.CertificationFeatureState.CertificationState;
  device: fromDevice.DeviceReducers.DeviceState;
  favoriteOccupations: fromFavoriteOccupations.FavoriteOccupationsFeatureState.FavoriteOccupationsState;
  favoriteMilitaryOccupations: fromFavoriteMilitaryOccupations.FavoriteMilitaryOccupationsFeatureState.FavoriteMilitaryOccupationsState;
  goal: fromGoal.GoalFeatureState.GoalState;
  hotspots: fromHotSpots.HotSpotsFeatureState.HotSpotsState;
  institution: fromInstitution.InstitutionFeatureState.InstitutionState;
  institutionProgram: fromInstitutionProgram.InstitutionProgramFeatureState.InstitutionProgramState;
  license: fromLicense.LicenseFeatureState.LicenseState;
  location: fromLocation.LocationFeatureState.LocationState;
  maintance: fromMaintance.MaintanceFeatureState.MaintanceState;
  major: fromMajor.MajorFeatureState.MajorState;
  news: fromNews.NewsFeatureState.NewsState;
  systemStatus: fromSystemStatus.SystemStatusFeatureState.SystemStatusState;
  occupation: fromOccupation.OccupationFeatureState.OccupationState;
  occupationDetail: fromOccupationDetail.OccupationDetailState.OccupationDetailState;
  occupationCards: fromOccupationCards.OccupationCardsState.OccupationCardsState;
  pathway: fromPathway.PathwayState;
  pathwayCards: fromPathwayCards.PathwayCardsState.PathwayCardsState;
  powerbi: fromPowerBi.PowerBiFeatureState.PowerBiState;
  tracker: fromTracker.TrackerFeatureState.TrackerState;
  userProfile: fromUserProfile.UserProfileFeatureState.UserProfileState;
  error: fromError.ErrorFeatureState.ErrorState;
  loader: fromLoader.LoaderState;
  user: fromUser.UserState;
  aiChat: fromAiChat.AiChatFeatureState.AiChatState;
}

export const reducers: ActionReducerMap<AppState> = {
  router: routerReducer,
  auth: fromAuth.AuthReducers.reducer,
  cognitoAuth: fromCognitoAuth.CognitoAuthReducers.reducer,
  certification: fromCertification.CertificationReducers.reducer,
  device: fromDevice.DeviceReducers.reducer,
  favoriteOccupations: fromFavoriteOccupations.FavoriteOccupationsReducers.reducer,
  favoriteMilitaryOccupations: fromFavoriteMilitaryOccupations.FavoriteMilitaryOccupationsReducers.reducer,
  goal: fromGoal.GoalReducers.reducer,
  hotspots: fromHotSpots.HotSpotsReducers.reducer,
  institution: fromInstitution.InstitutionReducers.reducer,
  institutionProgram: fromInstitutionProgram.InstitutionProgramReducers.reducer,
  license: fromLicense.LicenseReducers.reducer,
  location: fromLocation.LocationReducers.reducer,
  maintance: fromMaintance.MaintanceReducers.reducer,
  major: fromMajor.MajorReducers.reducer,
  news: fromNews.NewsReducers.reducer,
  systemStatus: fromSystemStatus.SystemStatusReducers.reducer,
  occupation: fromOccupation.OccupationReducers.reducer,
  occupationDetail: fromOccupationDetail.OccupationDetailReducers.reducer,
  occupationCards: fromOccupationCards.OccupationCardsReducer.reducer,
  pathway: fromPathway.reducer,
  pathwayCards: fromPathwayCards.PathwayCardsReducer.reducer,
  powerbi: fromPowerBi.PowerBiReducers.reducer,
  tracker: fromTracker.TrackerReducers.reducer,
  userProfile: fromUserProfile.UserProfileReducers.reducer,
  error: fromError.ErrorReducers.reducer,
  loader: fromLoader.reducer,
  user: fromUser.reducer,
  aiChat: fromAiChat.AiChatReducers.reducer,
};

export function storageSyncReducer(reducer: ActionReducer<AppState>) {
  return storageSync<AppState>({
    features: [
      { 
        stateKey: fromAuth.AuthFeatureState.authFeatureKey
      },
      { 
        stateKey: fromCognitoAuth.CognitoAuthFeatureState.cognitoAuthFeatureKey,
        storageForFeature: window.localStorage
      },
    ],
    storage: window.sessionStorage
  })(reducer);
}

// AD-3. clearStateOnAdminLogOut lives in its own file so it can be tested --
// this one imports @larscom/ngrx-store-storagesync, which pulls in lodash-es,
// which jest cannot parse.
//
// ORDER MATTERS. NgRx composes right to left, so listing the clear SECOND
// means it runs first, and storageSync then writes the CLEARED state back to
// sessionStorage and localStorage rather than re-persisting the stale one.
export const metaReducers: Array<MetaReducer<any, any>> = [
  storageSyncReducer,
  clearStateOnAdminLogOut
];

@Injectable()
export class CustomSerializer implements RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    let route = routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const {
      url,
      root: { queryParams },
    } = routerState;
    const { params } = route;

    // Only return an object including the URL, params and query params
    // instead of the entire snapshot
    return { url, params, queryParams };
  }
}